import { Children, cloneElement, isValidElement } from 'react';
import type { ReactElement, ReactNode } from 'react';

import { Button as DsButton, IconButton as DsIconButton, Tooltip as DsTooltip } from '@natter-co/design-system';
import { Button } from 'common/components/Buttons/Button/Button';
import { toDsProps as toDsButtonProps } from 'common/components/Buttons/Button/toDsProps';
import { IconButton } from 'common/components/Buttons/IconButton/IconButton';
import { toDsIconProps } from 'common/components/Buttons/IconButton/toDsIconProps';

import type { TooltipProps } from '@natter-co/design-system';
import type { ButtonProps } from 'common/components/Buttons/Button/Button';
import type { IconButtonProps } from 'common/components/Buttons/IconButton/IconButton';

type TriggerProps = ButtonProps & IconButtonProps & { loading?: boolean; 'aria-disabled'?: boolean | 'true' | 'false' };

const hasRenderFunction = (node: unknown): boolean =>
  typeof node === 'function' || (Array.isArray(node) && node.some(hasRenderFunction));

// Keep the caller's layout wrappers, but bind focus, touch and the description to the real button.
// Unknown or MUI-only disabled controls must retain the legacy tooltip instead.
export const toDsButtonTrigger = (
  children: ReactElement,
  tooltipProps: Pick<TooltipProps, 'label' | 'side' | 'align' | 'open'>
): ReactElement | null => {
  let found = 0;
  let unsupported = false;
  const visit = (node: ReactNode, depth = 0): ReactNode => {
    if (hasRenderFunction(node)) {
      unsupported = true;
      return node;
    }
    return Children.map(node, (child) => {
      if (!isValidElement<TriggerProps>(child)) return child;
      const props = child.props;
      const ariaLabel = props['aria-label'] ?? tooltipProps.label;
      const supported =
        child.type === DsButton ||
        child.type === DsIconButton ||
        (child.type === Button && !!toDsButtonProps(props)) ||
        (child.type === IconButton && !!toDsIconProps({ ...props, 'aria-label': ariaLabel }));
      const ariaDisabled = props['aria-disabled'] === true || props['aria-disabled'] === 'true';
      if (supported && (!ariaDisabled || props.disabled)) {
        found++;
        return (
          <DsTooltip {...tooltipProps}>
            {cloneElement(child, {
              focusableWhenDisabled:
                props.focusableWhenDisabled || props.disabled || props.isLoading || props.loading || undefined,
              ...(child.type === IconButton || child.type === DsIconButton ? { 'aria-label': ariaLabel } : {}),
            })}
          </DsTooltip>
        );
      }
      if (props.disabled || ariaDisabled) {
        unsupported = true;
        return child;
      }
      return depth < 3 && props.children ? cloneElement(child, { children: visit(props.children, depth + 1) }) : child;
    });
  };

  const mapped = visit(children);
  return found === 1 && !unsupported ? <>{mapped}</> : null;
};
